import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerFooter, DrawerClose } from './ui/drawer';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Clock, Calendar, MapPin, FileText, X, Check, Info, AlertCircle, Sparkles, ChevronRight } from 'lucide-react';
import { cn } from '../lib/utils';
import { useTranslation } from 'react-i18next';

const RecurringEventModal = ({ open, onOpenChange, onSubmit, editingEvent = null }) => {
    const { t } = useTranslation();
    const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
    const [formData, setFormData] = useState({
        title: '',
        days_of_week: [],
        time_of_day: '19:00',
        duration_minutes: 120,
        location: '',
        description: '',
        recurrence_end_date: ''
    });

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 1024);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Reset form when modal opens/closes or editingEvent changes
    useEffect(() => {
        if (open) {
            setFormData({
                title: editingEvent?.title || '',
                days_of_week: editingEvent?.days_of_week || [],
                time_of_day: editingEvent?.time_of_day || '19:00',
                duration_minutes: editingEvent?.duration_minutes || 120,
                location: editingEvent?.location || '',
                description: editingEvent?.description || '',
                recurrence_end_date: editingEvent?.recurrence_end_date || ''
            });
        }
    }, [open, editingEvent]);

    const daysOfWeek = [
        { value: 0, label: 'Pzt', fullLabel: 'Pazartesi' },
        { value: 1, label: 'Sal', fullLabel: 'Salı' },
        { value: 2, label: 'Çar', fullLabel: 'Çarşamba' },
        { value: 3, label: 'Per', fullLabel: 'Perşembe' },
        { value: 4, label: 'Cum', fullLabel: 'Cuma' },
        { value: 5, label: 'Cmt', fullLabel: 'Cumartesi' },
        { value: 6, label: 'Paz', fullLabel: 'Pazar' }
    ];

    const toggleDay = (dayValue) => {
        setFormData(prev => ({
            ...prev,
            days_of_week: prev.days_of_week.includes(dayValue)
                ? prev.days_of_week.filter(d => d !== dayValue)
                : [...prev.days_of_week, dayValue].sort()
        }));
    };

    const handleSubmit = (e) => {
        if (e) e.preventDefault();

        // Convert empty recurrence_end_date to null for backend validation
        const submitData = {
            ...formData,
            recurrence_end_date: formData.recurrence_end_date || null
        };

        onSubmit(submitData);
    };

    const FormContent = () => (
        <form onSubmit={handleSubmit} className="space-y-6 pb-20 lg:pb-0">
            {/* Title Input */}
            <div className="space-y-2">
                <div className="flex items-center gap-2 mb-1.5 px-0.5">
                    <div className="p-1.5 bg-snapp-purple/10 rounded-xl">
                        <Sparkles className="w-4 h-4 text-snapp-purple" />
                    </div>
                    <label className="text-xs font-black text-gray-400 uppercase tracking-widest">
                        {t('common.title') || 'Başlık'}
                    </label>
                </div>
                <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Örn: Haftalık İdman"
                    required
                    className="h-14 bg-white/5 border-white/10 text-white rounded-[1.2rem] px-5 focus:ring-snapp-purple/30 focus:border-snapp-purple/30 font-bold transition-all"
                    data-testid="recurring-title-input"
                />
            </div>

            {/* Day Picker */}
            <div className="space-y-4">
                <div className="flex items-center gap-2 mb-1 px-0.5">
                    <div className="p-1.5 bg-snapp-blue/10 rounded-xl">
                        <Calendar className="w-4 h-4 text-snapp-blue" />
                    </div>
                    <label className="text-xs font-black text-gray-400 uppercase tracking-widest">
                        {t('analytics.recurringDays') || 'Tekrarlayan Günler'}
                    </label>
                </div>
                <div className="flex flex-wrap lg:grid lg:grid-cols-7 gap-2">
                    {daysOfWeek.map((day) => {
                        const isSelected = formData.days_of_week.includes(day.value);
                        return (
                            <button
                                key={day.value}
                                type="button"
                                onClick={() => toggleDay(day.value)}
                                className={cn(
                                    "flex-1 min-w-[3.5rem] lg:min-w-0 h-14 rounded-2xl border transition-all duration-300 flex flex-col items-center justify-center gap-0.5 active:scale-90",
                                    isSelected
                                        ? "bg-gradient-to-br from-snapp-purple to-snapp-blue border-white/20 text-white shadow-lg shadow-snapp-purple/20 font-black"
                                        : "bg-white/5 border-white/5 text-gray-500 hover:text-gray-300 font-bold"
                                )}
                            >
                                <span className={cn("text-[10px] uppercase tracking-tighter opacity-70", isSelected ? "opacity-100" : "")}>
                                    {day.label}
                                </span>
                                <Check className={cn("w-3.5 h-3.5 transition-all duration-300", isSelected ? "scale-100 rotate-0" : "scale-0 rotate-45")} />
                            </button>
                        );
                    })}
                </div>
                {formData.days_of_week.length === 0 && (
                    <div className="flex items-center gap-2 text-[10px] font-black text-red-400 uppercase tracking-widest pl-1 mt-2">
                        <AlertCircle className="w-3.5 h-3.5" />
                        En az bir gün seçmelisiniz
                    </div>
                )}
            </div>

            {/* Time & Duration Grid */}
            <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                    <div className="flex items-center gap-2 mb-1.5 px-0.5">
                        <div className="p-1.5 bg-yellow-500/10 rounded-xl">
                            <Clock className="w-4 h-4 text-yellow-500" />
                        </div>
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                            {t('common.time') || 'Saat'}
                        </label>
                    </div>
                    <Input
                        type="time"
                        value={formData.time_of_day}
                        onChange={(e) => setFormData({ ...formData, time_of_day: e.target.value })}
                        required
                        className="h-12 bg-white/5 border-white/10 text-white rounded-[1rem] px-4 font-bold focus:ring-yellow-500/30 transition-all"
                        data-testid="recurring-time-input"
                    />
                </div>
                <div className="space-y-2">
                    <div className="flex items-center gap-2 mb-1.5 px-0.5">
                        <div className="p-1.5 bg-snapp-green/10 rounded-xl">
                            <Clock className="w-4 h-4 text-snapp-green" />
                        </div>
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest truncate">
                            {t('analytics.duration') || 'Süre (dk)'}
                        </label>
                    </div>
                    <Input
                        type="number"
                        value={formData.duration_minutes}
                        onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) })}
                        min="15"
                        step="15"
                        required
                        className="h-12 bg-white/5 border-white/10 text-white rounded-[1rem] px-4 font-bold focus:ring-snapp-green/30 transition-all"
                        data-testid="recurring-duration-input"
                    />
                </div>
            </div>

            {/* Location & End Date */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="space-y-2">
                    <div className="flex items-center gap-2 mb-1.5 px-0.5">
                        <div className="p-1.5 bg-red-500/10 rounded-xl">
                            <MapPin className="w-4 h-4 text-red-500" />
                        </div>
                        <label className="text-xs font-black text-gray-400 uppercase tracking-widest italic">
                            {t('common.location') || 'Konum'}
                        </label>
                    </div>
                    <Input
                        value={formData.location}
                        onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                        placeholder="Örn: Spor Salonu"
                        className="h-12 bg-white/5 border-white/10 text-white rounded-[1rem] px-4 font-bold placeholder:text-gray-600 focus:ring-red-500/30 transition-all"
                        data-testid="recurring-location-input"
                    />
                </div>
                <div className="space-y-2">
                    <div className="flex items-center gap-2 mb-1.5 px-0.5">
                        <div className="p-1.5 bg-blue-400/10 rounded-xl">
                            <Calendar className="w-4 h-4 text-blue-400" />
                        </div>
                        <label className="text-xs font-black text-gray-400 uppercase tracking-widest italic">
                            {t('analytics.endDate') || 'Bitiş'}
                        </label>
                    </div>
                    <Input
                        type="date"
                        value={formData.recurrence_end_date}
                        onChange={(e) => setFormData({ ...formData, recurrence_end_date: e.target.value })}
                        className="h-12 bg-white/5 border-white/10 text-white rounded-[1rem] px-4 font-bold focus:ring-blue-400/30 transition-all"
                        data-testid="recurring-end-date-input"
                    />
                </div>
            </div>

            {/* Description */}
            <div className="space-y-2">
                <div className="flex items-center gap-2 mb-1.5 px-0.5">
                    <div className="p-1.5 bg-gray-500/10 rounded-xl">
                        <FileText className="w-4 h-4 text-gray-400" />
                    </div>
                    <label className="text-xs font-black text-gray-400 uppercase tracking-widest italic">
                        {t('common.description') || 'Açıklama'}
                    </label>
                </div>
                <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Detaylar..."
                    className="bg-white/5 border-white/10 text-white rounded-[1.2rem] px-4 py-3 font-medium placeholder:text-gray-600 focus:ring-white/20 transition-all min-h-[100px]"
                    data-testid="recurring-description-input"
                />
            </div>
        </form>
    );

    if (isMobile) {
        return (
            <Drawer open={open} onOpenChange={onOpenChange}>
                <DrawerContent className="bg-[#1a1a1b] border-white/5 border-t text-white min-h-[85vh] rounded-t-[2.5rem] focus:outline-none ring-0">
                    <div className="mx-auto mt-4 h-1.5 w-12 rounded-full bg-white/10" />
                    <DrawerHeader className="px-6 pt-6 text-left">
                        <DrawerTitle className="text-2xl font-black text-white uppercase tracking-tight flex items-center gap-3">
                            <div className="p-3 bg-gradient-to-br from-snapp-purple to-snapp-blue rounded-2xl shadow-lg ring-1 ring-white/20">
                                <Calendar className="w-6 h-6 text-white" />
                            </div>
                            {editingEvent ? 'İdmanı Düzenle' : 'Tekrarlayan İdman'}
                        </DrawerTitle>
                    </DrawerHeader>

                    <div className="px-6 overflow-y-auto max-h-[70vh] custom-scrollbar">
                        <FormContent />
                    </div>

                    <div className="fixed bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-[#1a1a1b] via-[#1a1a1b] to-transparent pt-10">
                        <div className="flex gap-3">
                            <DrawerClose asChild>
                                <Button variant="ghost" className="flex-1 h-14 rounded-2xl text-gray-400 font-bold hover:bg-white/5 border border-white/5">
                                    {t('common.cancel') || 'İptal'}
                                </Button>
                            </DrawerClose>
                            <Button
                                onClick={handleSubmit}
                                disabled={formData.days_of_week.length === 0}
                                className="flex-[2] h-14 bg-gradient-to-br from-snapp-purple to-snapp-blue hover:opacity-90 text-white font-black rounded-2xl shadow-xl shadow-snapp-purple/20 transition-all active:scale-95 disabled:opacity-50 disabled:grayscale"
                            >
                                {editingEvent ? (t('common.update') || 'Güncelle') : (t('common.create') || 'Oluştur')}
                                <ChevronRight className="w-5 h-5 ml-2" />
                            </Button>
                        </div>
                    </div>
                </DrawerContent>
            </Drawer>
        );
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="bg-[#1a1a1b]/95 backdrop-blur-2xl border-white/5 text-white max-w-2xl rounded-[2.5rem] p-8 shadow-2xl ring-1 ring-white/10">
                <DialogHeader className="mb-6">
                    <DialogTitle className="text-3xl font-black text-white uppercase tracking-tight flex items-center gap-4">
                        <div className="p-3.5 bg-gradient-to-br from-snapp-purple to-snapp-blue rounded-[1.5rem] shadow-lg ring-1 ring-white/20">
                            <Calendar className="w-7 h-7 text-white" />
                        </div>
                        {editingEvent ? t('analytics.editRecurringTraining') : t('analytics.createRecurringTraining')}
                    </DialogTitle>
                </DialogHeader>

                <div className="max-h-[65vh] overflow-y-auto pr-4 custom-scrollbar">
                    <FormContent />
                </div>

                <DialogFooter className="mt-8 pt-6 border-t border-white/5 flex gap-4">
                    <Button
                        type="button"
                        variant="ghost"
                        onClick={() => onOpenChange(false)}
                        className="px-8 h-12 rounded-xl text-gray-400 font-bold hover:bg-white/5"
                    >
                        {t('common.cancel') || 'İptal'}
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={formData.days_of_week.length === 0}
                        className="px-10 h-12 bg-gradient-to-br from-snapp-purple to-snapp-blue hover:opacity-90 text-white font-black rounded-xl shadow-lg shadow-snapp-purple/20 transition-all active:scale-95 disabled:opacity-50"
                    >
                        {editingEvent ? (t('common.update') || 'Güncelle') : (t('common.create') || 'Oluştur')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default RecurringEventModal;
